/**
 * Keep alive component
 * https://stackoverflow.com/questions/59124463/nextjs-how-to-not-unmount-previous-page-when-going-to-next-page-to-keep-state
 *
 * Tried following libraries before implementing this one:
 * - react-keep-alive: has a lot of dom manipulation bugs and crashes our app
 * - react-activation: does not handle SSR correctly
 * - there are some others but they are built on react-router, which next.js doesn't use
 *
 * Basic Usage:
 * 1) Wrap nextjs <Component /> with <KeepAliveProvider> in _app.tsx
 * 2) Wrap components export with "withKeepAlive" and provide unique name like this: export default withKeepAlive(IndexPage, 'index');
 */
import React, { useRef, memo, useEffect, ReactElement, cloneElement, Fragment, useState, EffectCallback } from 'react';
import { NextRouter, useRouter } from 'next/router'; // eslint-disable-line import/no-extraneous-dependencies
import { keepAliveDropCache } from './controls';
import eventemitter, { actions } from '@/utils/eventemitter';
import { hideLoading, showLoading } from '@/components/common/Indicator/pageIndicator';
import { getPureUrl } from '@/utils/url';

export type KeepAliveCacheType = {
  [name: string]: {
    Component: any;
    pageProps: any;
    scrollPos?: number;
    name?: string;
    enabled?: boolean;
    isBack?: boolean;
    route?: string;
  };
};

type KeepAliveNameFnArgs = {
  props: any;
  router: NextRouter;
};

export type KeepAliveName = string | ((nameArgs: KeepAliveNameFnArgs) => string);

export type KeepAliveOptsProps = {
  keepScrollEnabled?: boolean;
  applyNewProps?: boolean | ((cachedProps: any, newProps: any) => Object);
};

type KeepAliveData = {
  name: KeepAliveName;
} & KeepAliveOptsProps;

type ExtendChildrenType = {
  type: {
    keepAlive: KeepAliveData;
  };
};

type KeepAliveProviderProps = {
  children: ReactElement & ExtendChildrenType;
};

const defaultEnabled = true;

const KeepAliveProvider = (props: KeepAliveProviderProps) => {
  const { children } = props;
  const router = useRouter();

  const pageProps = children?.props;
  const componentData = cloneElement(children);
  const keepAliveCache = useRef<KeepAliveCacheType>({});
  const [, forceUpdate] = useState<any>();
  const keepAliveLastestCache = useRef<string>('');
  const [tempIgnoreKeepScrollEnabled, setTempIgnoreKeepScrollEnabled] = useState<boolean>(false);
  /**
   * Whether current navigation is triggered by browser back/forward (popstate).
   * We only restore cached view & scroll position for popstate navigations.
   */
  const isPopStateNavRef = useRef(false);
  const isShallowNavRef = useRef(false);

  const {
    name: keepAliveName,
    keepScrollEnabled,
    applyNewProps
    // @ts-ignore
  } = componentData?.type?.keepAlive || {};
  const name = typeof keepAliveName === 'function' ? keepAliveName({ props: pageProps, router }) : keepAliveName;

  const isKeptAlive = !!name;

  // Add Component to retainedComponents if we haven't got it already
  if (isKeptAlive && !keepAliveCache.current[name]) {
    const Component: any = componentData?.type;
    const MemoComponent = memo(Component);
    keepAliveCache.current[name] = {
      Component: MemoComponent,
      pageProps: pageProps,
      scrollPos: 0,
      name,
      enabled: defaultEnabled,
      isBack: false,
      route: router.route
    };
  }
  // KeepAlive name
  const isEnabled = () => keepAliveCache?.current?.[name]?.enabled;
  const keepScroll = !!keepScrollEnabled;

  // Save the scroll position of current page before leaving
  const handleRouteChangeStart = (url: string, { shallow }: { shallow: boolean }) => {
    isShallowNavRef.current = shallow;
    showLoading();
    if (isKeptAlive && keepAliveCache?.current?.[name]) {
      keepAliveCache.current[name].scrollPos = window.scrollY;
    }

    // 只有在非浏览器后退/前进（popstate）的情况下，才考虑清理缓存。
    // 这类导航通常是点击链接或手动调用 router.push/replace 触发的。
    if (!isPopStateNavRef.current) {
      const locales = router.locales || [];

      const normalizedTargetPath = getPureUrl(locales, url);
      const normalizedCurrentPath = getPureUrl(locales, router.asPath);

      /**
       * 这里判断路由是否是真的跳转：
       * 1. /search -> /search?q=1 不算真跳转
       * 2. router.replace 不算真跳转
       * 如果是页面内部的跳转，则不清理缓存，比如改变查询条件后的replace
       * 否则清楚同样url页面的KeepAlive缓存
       */
      if (normalizedTargetPath !== normalizedCurrentPath) {
        // 查找所有匹配目标路由的缓存 key
        const cacheKeysToRemove = Object.keys(keepAliveCache.current).filter(key => {
          const entry = keepAliveCache.current[key];
          // 匹配 entry.route (Next.js 路由模板)
          return entry.route === normalizedTargetPath || entry.route === `${normalizedTargetPath}/index`;
        });

        // 彻底释放缓存，确保下次重新进入时是一个干净的页面
        if (cacheKeysToRemove.length > 0) {
          cacheKeysToRemove.forEach(key => delete keepAliveCache.current[key]);
          forceUpdate({});
        }
      }
    }
  };

  // Restore scroll only when user navigated back (popstate). Forward navigation (e.g. link) should not restore.
  const handleKeepScroll = () => {
    if (isKeptAlive && isEnabled() && keepScroll) {
      if (!isPopStateNavRef.current) return;
      let scrollPosition = keepAliveCache.current[name]?.scrollPos;
      if (tempIgnoreKeepScrollEnabled) {
        scrollPosition = 0;
      }
      // 初始化等滑动距离为0的场景，跳过scrollTo
      if (!scrollPosition) return;
      window.scrollTo(0, scrollPosition || 0);
      // Just in case try again in next event loop
      setTimeout(() => {
        window.scrollTo(0, scrollPosition || 0);
      }, 0);
    }
  };

  // 最多保留2个页面, 超出的drop最后一个keepalive的页面
  const handleKeepAliveCountLimit = () => {
    const cacheKeys = Object.keys(keepAliveCache.current);
    const cacheKeysLength = cacheKeys.length;
    if ((cacheKeysLength === 2 && !isKeptAlive) || cacheKeysLength > 2) {
      // 保留最后一个，剩下的drop
      const cacheKeysToRemove = cacheKeys.filter(key => key !== keepAliveLastestCache.current);
      keepAliveDropCache(() => cacheKeysToRemove);
    }
    keepAliveLastestCache.current = name;
  };

  // Enable/disable loading from cache
  const handleLoadFromCache = (event: any) => {
    const { name: controlsName, enabled: controlsEnabled } = event?.detail || {};

    if (keepAliveCache.current[controlsName]) {
      keepAliveCache.current[controlsName].enabled = controlsEnabled;
    }
  };

  // Drop cache
  const handleDropCache = (event: any) => {
    const { name: dropKeepAliveName, scrollToTop } = event?.detail || {};

    // If no name, drop all cache
    if (!dropKeepAliveName) {
      keepAliveCache.current = {};
    } else if (typeof dropKeepAliveName === 'string') {
      delete keepAliveCache.current?.[dropKeepAliveName];
    } else if (typeof dropKeepAliveName === 'function') {
      const caches = dropKeepAliveName?.(Object.keys(keepAliveCache.current));
      const cachesToRemove: string[] = Array.isArray(caches) ? caches : [caches];

      // eslint-disable-next-line no-unused-expressions
      cachesToRemove?.filter(exists => exists)?.forEach(cacheName => delete keepAliveCache.current?.[cacheName]);
    }
    if (scrollToTop && typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }

    forceUpdate({});
  };

  useEffect(() => {
    const callback = (navigatingToPath: any) => {
      if (navigatingToPath == router.asPath) {
        setTempIgnoreKeepScrollEnabled(true);
      }
    };
    eventemitter.on(actions.blockRoutePushScrollRestoration, callback);

    return () => {
      eventemitter.off(actions.blockRoutePushScrollRestoration, callback);
    };
  }, []);

  // Handle scroll position caching - requires an up-to-date router.asPath
  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.asPath]);

  // Detect browser back/forward navigations (popstate).
  useEffect(() => {
    router.beforePopState(() => {
      isPopStateNavRef.current = true;
      return true;
    });
    return () => {
      router.beforePopState(() => true);
    };
  }, []);

  // Emit mounting events
  // @ts-ignore
  useEffect(() => {
    handleKeepAliveCountLimit();
    if (isKeptAlive) {
      window.dispatchEvent(
        new CustomEvent('onKeepAliveMount', {
          detail: name
        })
      );
      handleKeepScroll();
      // Consume popstate flag after restoration on this activation.
      isPopStateNavRef.current = false;
      return () => {
        window.dispatchEvent(
          new CustomEvent('onKeepAliveUnmount', {
            detail: name
          })
        );
      };
    }
    setTempIgnoreKeepScrollEnabled(false);
    hideLoading();

    // Safari may preserve the previous page's scroll position during client-side navigation,
    // even with scrollRestoration set to 'manual'. Reset scroll for non-KeepAlive pages,
    // but skip for popstate (back/forward) and shallow (same-page URL update) navigations.
    if (!isPopStateNavRef.current && !isShallowNavRef.current) {
      window.scrollTo(0, 0);
    }
    isPopStateNavRef.current = false;
    isShallowNavRef.current = false;
  }, [name]);

  /**
   * Listen to changes (enabled/disabled)
   */
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.addEventListener('keepAliveControls_LoadFromCache', handleLoadFromCache);
    window.addEventListener('keepAliveControls_DropCache', handleDropCache);

    return () => {
      window.removeEventListener('keepAliveControls_LoadFromCache', handleLoadFromCache);
      window.removeEventListener('keepAliveControls_DropCache', handleDropCache);
    };
  }, []);

  const getCachedViewProps = (cachedProps: Object, cacheName: string) => {
    // Only apply new props to the currently active cached page; hidden pages keep their cached props
    if (applyNewProps === true) {
      return cacheName === name ? pageProps : cachedProps;
    }

    // Apply combination of old and new props
    if (typeof applyNewProps === 'function') {
      return applyNewProps(cachedProps, pageProps);
    }

    // Apply cached props
    return cachedProps;
  };

  /**
   * Custom useEffect which runs only when component alive.
   */
  const getKeepAliveEffect = (isHidden: boolean) => {
    const useKeepAliveEffect = (effect: EffectCallback, deps?: any[]) =>
      useEffect(() => {
        if (!isHidden) {
          return effect();
        }
      }, deps);

    return useKeepAliveEffect;
  };

  return (
    // eslint-disable-next-line react/jsx-fragments
    <Fragment>
      {(!isKeptAlive || !isEnabled()) && children}

      <div
        style={{ display: isKeptAlive && isEnabled() ? 'block' : 'none' }}
        id="keep-alive-container"
        data-keepalivecontainer={true}>
        {Object.entries(keepAliveCache.current).map(([cacheName, { Component, pageProps: cachedProps }]: any) => (
          <div
            key={cacheName}
            style={{ display: name === cacheName ? 'block' : 'none' }}
            data-keepalive={cacheName}
            data-keepalive-hidden={name !== cacheName}>
            <Component
              isHiddenByKeepAlive={name !== cacheName}
              useEffect={getKeepAliveEffect(name !== cacheName)}
              {...getCachedViewProps(cachedProps, cacheName)}
            />
          </div>
        ))}
      </div>
    </Fragment>
  );
};

export default KeepAliveProvider;
